import React, { Component } from 'react';
import PropTypes from 'prop-types';

import ListView from '../Common/ListView.react';
import EventListItem from './EventListItem.react';
import EventCreateOptionsDialog from './EventCreateOptionsDialog';
import FlatButton from 'material-ui/FlatButton';
import LinearProgress from 'material-ui/LinearProgress';

import CancelIcon from 'material-ui/svg-icons/navigation/cancel';
import CalendarIcon from 'material-ui/svg-icons/action/date-range';
import EventIcon from 'material-ui/svg-icons/action/event';
import UpIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-up';
import DownIcon from 'material-ui/svg-icons/hardware/keyboard-arrow-down';

import EventStore from '../../stores/EventStore';
import EventActions from '../../actions/EventActionCreator';

import buildContextMenu from '../Common/BuildContextMenu';

class EventList extends Component {

    constructor() {
        super(...arguments);
        this.state = {
            actions: [],
            events: [],
            count: 0,
            page: 0,
            limit: 25,
            searchPage: 0,
            searchMode: false,
            searchText: null,
            dialogOpen: false,
            sortCriteria: null,
            sortMapCriteria: null,
            loading: false,
            fetchedAll: false,
            showButton: false
        };
    }

    componentDidMount() {
        this.onChange = this.onChange.bind(this);
        this.sort = this.sort.bind(this);
        this.loadNextPage = this.loadNextPage.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.search = this.search.bind(this);
        this.toggleSearchMode = this.toggleSearchMode.bind(this);

        EventStore.addChangeListener(this.onChange);
        EventActions.getAll({startDate: new Date().toDateString()});
        this.setState({actions: buildContextMenu(this.getActions(), this.context.auth)});

        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount() {
        EventStore.removeChangeListener(this.onChange);
        window.removeEventListener('scroll', this.handleScroll);
    }

    getActions() {
        return [
            {
                link: '/dashboard/events/calendar',
                icon: <CalendarIcon />,
                label: 'Calendar View',
            },
            {
                action: () => {this.setState({dialogOpen: true})},
                icon: <EventIcon />,
                label: 'Create from template',
            },
            {
                type: 'divider'
            },
            {
                link: '/logout',
                icon: <CancelIcon />,
                label: 'Sign out',
            },
        ];
    }

    render() {
        const listViewProps = {
            title: "Events",
            sortOptions: this.props.sortOptions,
            sortAction: this.sort,
            searchAction: this.search,
            actions: this.state.actions,
            items: this.state.events,
            itemDecorator: EventListItem,
            newEntityUrl: '/dashboard/event/new',
            toggleSearchMode: this.toggleSearchMode
        };

        return (
            <div ref="eventList">
                <ListView {...listViewProps} />
                {this.state.loading && (
                    <LinearProgress mode="indeterminate" />
                )}
                {this.state.showButton && (
                    <FlatButton
                        label={this.state.fetchedAll ? 'Back to top' : 'Load more'}
                        icon={this.state.fetchedAll ? <UpIcon/> : <DownIcon/>}
                        onTouchTap={this.state.fetchedAll ? () => window.scrollTo(0, 0) : this.loadNextPage}
                        disabled={this.state.loading}
                        fullWidth
                    />
                )}
                <EventCreateOptionsDialog
                    title="Create new event"
                    open={this.state.dialogOpen}
                    onCancel={() => this.setState({dialogOpen: false})}
                />
            </div>
        );
    }

    onChange() {
        const events = EventStore.getByDateRange(new Date());
        if (!!events) {
            if (EventStore.fetchedAll()) {
                this.setState({fetchedAll: true});
            }
            if (events.length <= 1 || !this.state.sortCriteria || !this.state.sortMapCriteria) {
                this.setState({events, loading: false});
            } else {
                const mapped = events.map(this.state.sortMapCriteria);
                mapped.sort(this.state.sortCriteria);
                const sortedEvents = mapped.map((el) => events[el.index]);
                this.setState({events: sortedEvents, loading: false});
            }
        }
    }

    sort(options) {
        let sortCriteria, sortMapCriteria;
        switch (options.sort) {
            case 'name':
                sortCriteria = (a, b) => a.value.localeCompare(b.value);
                sortMapCriteria = (el, i) => ({index: i, value: el.name});
                break;
            case 'timeStart':
                sortCriteria = (a, b) => (a.value - b.value);
                sortMapCriteria = (el, i) => ({index: i, value: Date.parse(el.timeStart)});
                break;
            case 'venue':
                sortCriteria = (a, b) => a.value.localeCompare(b.value);
                sortMapCriteria = (el, i) => ({index: i, value: !!el.venue ? el.venue : ''});
                break;
            case 'status':
                sortCriteria = (a, b) => a.value.localeCompare(b.value);
                sortMapCriteria = (el, i) => ({index: i, value: el.status});
                break;
            default:
                sortCriteria = (a, b) => (a.value - b.value);
                sortMapCriteria = (el, i) => ({index: i, value: el[this.state.sort]});
        }
        this.setState({sort: options.sort, sortCriteria, sortMapCriteria});
        EventActions.getAll({sort: options.sort, startDate: new Date().toDateString()});
    }

    loadNextPage() {
        if (this.state.loading) { return; } //guard

        const page = !this.state.searchMode ? (this.state.page + 1) : (this.state.searchPage + 1);
        if (!this.state.searchMode) {
            EventActions.getAll({page, startDate: new Date().toDateString()});
            this.setState({page, loading: true});
        } else if (!!this.state.searchText) {
            EventActions.search(this.state.searchText, {page, limit: this.state.limit});
            this.setState({searchPage: page, loading: true});
        }
    }

    handleScroll() {
        if (!this.state.showButton && window.scrollY > 0) {
            this.setState({showButton: true})
        }
        if (!this.state.fetchedAll && (window.scrollY + window.innerHeight) >= this.refs.eventList.clientHeight) {
            this.loadNextPage();
        }
    }

    search(searchText) {
        this.timeout && clearTimeout(this.timeout);
        if (!!searchText && searchText.length > 2) {
            this.timeout = setTimeout(() => { EventActions.search(searchText, {limit: this.state.limit}); }, 500);

            if (searchText !== this.state.searchText) {
                this.setState({fetchedAll: false, showButton: false});
            }
            this.setState({searchText, searchPage: 0});
        }
    }

    toggleSearchMode(searchMode) {
        if (!searchMode) {
            this.setState({searchText: null});
        }
        this.setState({searchMode, fetchedAll: false, showButton: false});
    }

}

EventList.defaultProps = {
    sortOptions: [
        {label: "Name", value: 'name'},
        {label: "Date", value: 'timeStart'},
        {label: "Venue", value: 'venue'},
    ]
};

EventList.contextTypes = {
    auth: PropTypes.object.isRequired
};

export default EventList;
